export default interface Prescription {
	shellModifications: ShellModifications;
	postingOptions: PostingOptions;
	accommodations: Accommodations;
	topCovers: TopCovers;
	uiContextData: UiContextData;
}

export type ArchModificationOptions = 'none' | 'increase-3' | 'increase-6' | 'increase-12' | 'decrease-3' | 'decrease-6';
export interface ArchModification {
	left: ArchModificationOptions;
	right: ArchModificationOptions;
}

export interface FlangeOptions {
	side: LROptions;
	value?: MedLowHighExtra;
}

export interface ShellModifications {
	shellRigidity: 'rigid' | 'flexible' | 'semi' | 'super-flex';
	shellRigidityOption?: 'very-soft' | 'semi-soft' | 'soft' | 'semi-hard' | 'hard' | 'very-hard';
	shellLength: 'met' | 'sulcus' | 'full';
	shellShape: 'standard' | 'hourglass' | 'medial-deformity' | 'lateral-deformity';
	heelCutout: LROptions;
	firstRayCutout: {
		side: LROptions;
		value?: '45deg' | '60deg';
	};
	reverseMortonsExtension: {
		type: 'none' | 'flexible' | 'semi-rigid';
		value?: ExtensionThicknessValue;
		side: LROptionsWithoutNone;
	};
	heelSeat: '3' | '6' | '9' | '12' | '15' | '18' | '21';
	archModification: ArchModification;
	archReinforcement: {
		side: LROptions;
		value?: '1.5mm' | '3mm' | '6mm';
		pattern: 'honeycomb' | 'filled';
	};
	medialFlange: FlangeOptions;
	lateralFlange: FlangeOptions;
	cutoutAndStyloid: LROptions;
	cutoutAndFirst: LROptions;
	cutoutFifth: LROptions;
	navicularWell: LROptions;
	wellAndStyloid: LROptions;
	mortonsExtension: {
		type: 'none' | 'flexible' | 'semi-rigid' | 'rigid';
		value?: ExtensionThicknessValue;
		side: LROptionsWithoutNone;
	};
	shellSpecialRX: string[]; // FIXME: Should be enum if possible
}

export interface PostingOptions {
	heelLift: HeelLift;
	medialHeelSkive: Skive;
	lateralHeelSkive: Skive;
	rearfootPost: RearfootPost;
	forefootPost: ForefootPost;
	rearfootPostUndercut?: RearfootPostUndercut;
}

export interface TopCovers {
	topCoverMaterial: CoverMaterialValue;
	midLayerMaterial: MidMaterialValue;
	bottomCover: BottomCoverMaterialValue;
	topCoverLength: 'met' | 'sulcus' | 'full';
	widenTopcoverAtForefootTwoSizes: LROptions;
	navicularWing: LROptions;
	coverSpecialRX: string[]; // FIXME: Should be enum if possible
}

export interface Accommodations {
	heelCushion: {
		side: LROptions;
		value?: '1.5mm' | '3mm';
	};
	mortonsExtensionEva: LROptions;
	heelSpurPad: {
		side: LROptions;
		value?: '1.5mm' | '3mm';
	};
	metBar: {
		side: LROptions;
		value?: '1.5mm' | '3mm';
	};
	dancerPad: {
		side: LROptions;
		value?: '1.5mm' | '3mm';
	};
	metPads: {
		side: LROptions;
		value?: '1.5mm' | '3mm' | '5mm';
	};
	neuromaPad: {
		left: NeuromaPadValues[];
		right: NeuromaPadValues[];
	};
	balancePad: {
		left: BalancePadValues[];
		right: BalancePadValues[];
	};
	forefootExtension: {
		side: LROptions;
		value?: '1.5mm' | '3mm';
	};
	dispersionPadHallux: LROptions;
	dispersionPadAtNavicular: LROptions;
	dispersionPadAtStyloidProcess: LROptions;
	extrinsicForefootPost: ExtrinsicForefootPost;
	accommodationsSpecialRX: string[]; // FIXME: Should be enum if possible
}

export type LROptions = 'left' | 'right' | 'none' | 'both';
export type LRStrictOptions = 'left' | 'right';
export type LROptionsWithoutNone = 'left' | 'right' | 'both';

export type NeuromaPadValues = '1' | '2' | '3' | '4';
export type BalancePadValues = '1' | '2' | '3' | '4' | '5';

export type HeelLiftValue = '0mm' | '1mm' | '2mm' | '3mm' | '4mm' | '5mm' | '6mm' | '7mm' | '8mm';
export type SkiveValue = '0mm' | '2mm' | '4mm' | '6mm' | '8mm';
export type ForefootPostValue = '0deg' | '1deg' | '2deg' | '3deg' | '4deg' | '5deg' | '6deg';

export interface HeelLift {
	left: HeelLiftValue;
	right: HeelLiftValue;
}

export interface Skive {
	left: SkiveValue;
	right: SkiveValue;
}

export interface RearfootPost {
	override: 'none' | 'lab-specifies' | 'specify';
	left?: Forefoot;
	right?: Forefoot;
}

type ForefootWithPattern = Forefoot & { pattern: 'honeycomb' | 'filled' | 'hollow' };

export interface ForefootPost {
	override: 'none' | 'balance' | 'extrinsic' | 'intrinsic';
	left?: ForefootWithPattern;
	right?: ForefootWithPattern;
}

export interface ExtrinsicForefootPost {
	override: 'none' | 'balance-to-rearfoot' | 'specify';
	left?: Forefoot;
	right?: Forefoot;
}

export type RearfootPostUndercut = 'narrow' | 'standard' | 'wide';

export interface Forefoot {
	type: 'varus' | 'valgus';
	value: ForefootPostValue;
}

export type ExtensionThicknessValue = 'consistent' | '2.5mm';
export type MedLowHigh = 'medium' | 'low' | 'high' | 'x-high';
export type MedLowHighExtra = 'medium' | 'low' | 'high' | 'x-high';

type CoverMaterialValue =
	| 'eva-black-1-16'
	| 'eva-black-1-8'
	| 'neolon-black-1-16'
	| 'neolon-black-1-8'
	| 'plastazote-1-8'
	| 'poly-u-1-16-vinyl'
	| 'black-vinyl'
	| 'black-microsuede'
	| 'ucolite-black-perforated-1-16';

type MidMaterialValue = 'none' | 'eva-black-1-16' | 'eva-black-1-8' | 'poly-u-1-16' | 'poly-u-1-8';

type BottomCoverMaterialValue = 'none' | 'suede' | 'vinyl' | 'eva-black-1-24';

export type ShoeStandard = 'us-mens' | 'us-womens' | 'uk-mens' | 'uk-womens' | 'eu-mens' | 'eu-womens' | 'cm-mens' | 'cm-womens';
export type ShoeWidth = 'standard' | 'narrow' | 'wide' | 'x-wide';

export interface UiContextData {
	weight: WeightInput;
	scanMode: 'semi-weighted' | 'weighted' | 'non-weighted' | 'foambox';
	shoeStandard: {
		left: ShoeStandard;
		right: ShoeStandard;
	};
	shoeSize: {
		left: string;
		right: string;
	};
	shoeWidth: {
		left: ShoeWidth;
		right: ShoeWidth;
	};
	orthoticStyle: 'standard' | 'stability' | 'comfort' | 'sports' | 'dress';
	quantity: number;
	pack: LROptionsWithoutNone;
	zebraID: string;
	expertReview: boolean;
	orthoticStyleTitle?: string;
}

export interface WeightInput {
	value: string;
	unit: 'lbs';
}

export const SHELL_SPECIAL_RX = [
	'Hourglass the shell',
	'Widen forefoot at shell',
	'Cutout at 5th MPJ',
	'Cut out at base of 5th met',
	'Accommodate the base of 5th met',
	'Accommodate the navicular',
	'Accommodate the heel'
] as const;

export const ACCOMMODATIONS_SPECIAL_RX = [
	'1/16" poron backfill',
	'EVA reinforcement from heel to mets',
	'EVA arch reinforcement',
	'1/16" EVA distal FF extension',
	'Extrinsic tip post lateral',
	'Extrinsic tip post medial',
	'Extrinsic FF post lateral',
	'Extrinsic FF post medial',
	'Extrinsic lateral skive from heel to FF',
	'FF extrinsic medial post to balance FF to RF to sulcus & thin edge',
	'Dispersion pad at Hallux',
	'Dispersion pad at Navicular',
	'Dispersion pad at base of 5th met'
] as const;

export const COVER_SPECIAL_RX = ['Widen top cover throughout 2 sizes', 'Widen top cover at forefoot only 2 sizes', 'Add navicular wing'] as const;

type ShellSpecialRX = typeof SHELL_SPECIAL_RX[number];
type AccommodationsSpecialRX = typeof ACCOMMODATIONS_SPECIAL_RX[number];
type CoverSpecialRX = typeof COVER_SPECIAL_RX[number];
